

/** Keys that are known runtime back-references — always dropped when cloning. */
const BACK_REF_KEYS = new Set(['parent']);

/** Deep-clone `value` into a JSON-safe structure: strips known back-reference
 *  keys and breaks any true cycle (an object referencing one of its ancestors).
 *  Shared but non-cyclic references are preserved as separate copies. */
export function toSerializable<T>(value: T): T {
  const clone = (v: unknown, ancestors: Set<unknown>): unknown => {
    if (typeof v === 'function') return undefined;
    if (v === null || typeof v !== 'object') return v;
    if (ancestors.has(v)) return undefined; // ancestor cycle — cut it
    const nextAncestors = new Set(ancestors).add(v);
    if (Array.isArray(v)) return v.map((item) => clone(item, nextAncestors));
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(v as Record<string, unknown>)) {
      if (BACK_REF_KEYS.has(key)) continue;
      out[key] = clone((v as Record<string, unknown>)[key], nextAncestors);
    }
    return out;
  };
  return clone(value, new Set()) as T;
}

const WIDGET_DATA_KEYS = ['staticData', 'data', 'drilldownData', 'waterfallBars', 'option', 'rows', 'responseList'];
/** Input controls: their value/options ARE config, not computed data. */
const INPUT_WIDGET_TYPES = new Set(['slider', 'input-box', 'dropdown', 'file-upload', 'popup']);

/** Drop a published widget's baked rows — the published view takes its data from
 *  each component's compute call, so the snapshot is dead weight in the DB row. */
export function stripWidgetData<T extends Record<string, unknown>>(widget: T, keepData = false): T {
  if (keepData || !widget || INPUT_WIDGET_TYPES.has(String(widget['type'] ?? '').toLowerCase())) return widget;
  const out = { ...(widget as Record<string, unknown>) };
  WIDGET_DATA_KEYS.forEach((key) => delete out[key]);
  return out as T;
}
